import type { Approval, FlowVersion } from "@zworks/console-contracts";
import { Button } from "@/components/ui/button";
import type { StepChange } from "@/lib/flow-diff";
import type { FlowStep } from "@zworks/console-contracts";

export interface FlowPublishPanelProps {
  /** The latest published version, which the draft is compared with and the version pin names. */
  latest: FlowVersion | null;
  /** Each step of the draft as added, removed, or unchanged against the latest version. */
  changes: { step: FlowStep; change: StepChange }[];
  /** True when a step deploys, so publishing needs an approver's confirmation. */
  deploys: boolean;
  /** The publish approval this editor requested, read from the approvals inbox. */
  approval: Approval | null;
  canEdit: boolean;
  busy: boolean;
  onRequestApproval: () => void;
  onRefreshApproval: () => void;
}

/**
 * The publish panel: the version pin as a labelled badge, the changes since that version, and the
 * approval a deploying workflow needs before it publishes. The server decides every outcome; the
 * panel only says what the server reported.
 */
export function FlowPublishPanel({
  latest,
  changes,
  deploys,
  approval,
  canEdit,
  busy,
  onRequestApproval,
  onRefreshApproval,
}: FlowPublishPanelProps) {
  const changed = changes.filter((row) => row.change !== "unchanged");
  const nextNumber = (latest?.number ?? 0) + 1;

  return (
    <section aria-label="Publishing" className="flex flex-col gap-2 text-xs shrink-0">
      <div className="flex flex-wrap items-center gap-2">
        {latest ? (
          <span
            data-testid="version-pin"
            aria-label={`Version pin: version ${latest.number}`}
            className="rounded-md border px-2 py-0.5 font-medium"
          >
            Version pin · v{latest.number}
          </span>
        ) : (
          <span className="text-muted-foreground">No published version yet</span>
        )}
        {deploys ? <span className="text-muted-foreground">This workflow deploys.</span> : null}
      </div>

      {latest ? (
        <div>
          <p className="font-medium">
            {changed.length === 0
              ? `No changes since version ${latest.number}.`
              : `Changes since version ${latest.number}`}
          </p>
          {changed.length > 0 ? (
            <ul aria-label="Changes since the latest version" className="list-disc pl-5">
              {changed.map((row, index) => (
                <li key={`${row.change}-${row.step.name}-${index}`}>
                  {row.change === "added" ? "Added" : "Removed"}: {row.step.name}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {deploys ? (
        <div className="flex flex-wrap items-center gap-2">
          {approval === null ? (
            <>
              <span>
                An owner or approver must confirm this change before version {nextNumber} publishes.
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!canEdit || busy}
                onClick={onRequestApproval}
              >
                Request approval
              </Button>
            </>
          ) : approval.status === "pending" ? (
            <>
              <span>Waiting for an owner or approver to confirm this change.</span>
              <Button variant="outline" size="sm" onClick={onRefreshApproval}>
                Refresh approval
              </Button>
            </>
          ) : approval.status === "approved" ? (
            <span>Approved. Activate to publish version {nextNumber}.</span>
          ) : (
            <>
              <span>
                Declined{approval.reason ? `: ${approval.reason}` : ""}. Change the workflow, then
                request approval again.
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!canEdit || busy}
                onClick={onRequestApproval}
              >
                Request approval
              </Button>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
